import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, Plus, Search } from 'lucide-react'
import { getVendors } from '../../api/master'
import Spinner from '../../components/Spinner'
import ErrorBanner from '../../components/ErrorBanner'
import { downloadCsv } from '../accounting/reportsShared'
import { MasterTabs, placeLine } from './masterShared'

// Vendors: the suppliers you buy goods and services from.
export default function VendorList() {
  const [search, setSearch] = useState('')
  const { data: rows = [], isLoading, error } = useQuery({ queryKey: ['vendors'], queryFn: getVendors })
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return rows
      .filter((v) => !term || [v.name, v.contact_name, v.email, v.phone, v.city].join(' ').toLowerCase().includes(term))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
  }, [rows, search])

  const exportCsv = () => downloadCsv('vendors', [
    ['Name', 'Contact', 'Email', 'Phone', 'Address', 'City', 'State', 'Postal code', 'Country'],
    ...shown.map((v) => [v.name, v.contact_name, v.email, v.phone, v.address, v.city, v.state, v.postal_code, v.country]),
  ])

  return (
    <div className="ci-page">
      <MasterTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Vendors</h1>
            <div className="ci-title-meta">The suppliers you buy goods and services from.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!shown.length}><Download size={15} />Export</button>
          <Link className="ci-btn ci-btn-primary" to="/master/vendors/new"><Plus size={15} />New vendor</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search vendors">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, contact, email or city…" />
        </label>
      </div>

      {error && <ErrorBanner message="Couldn’t load the vendors." />}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead><tr><th>Vendor</th><th className="hide-sm">Contact</th><th className="hide-sm">Email</th><th className="hide-sm">Phone</th><th className="hide-sm">Place</th></tr></thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="5"><Spinner label="Loading vendors…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="5"><div className="ci-empty">{rows.length ? 'No vendors match.' : <>No vendors yet. <Link to="/master/vendors/new">Add your first vendor</Link>.</>}</div></td></tr>
              ) : shown.map((v) => (
                <tr key={v.vendor_id}>
                  <td>
                    <Link className="ci-strong mf-row-link" to={`/master/vendors/${v.vendor_id}`}>{v.name}</Link>
                    <span className="cell-sub show-sm-block">{[v.contact_name, v.phone].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td className="hide-sm">{v.contact_name || <span className="ci-muted-cell">–</span>}</td>
                  <td className="hide-sm">{v.email || <span className="ci-muted-cell">–</span>}</td>
                  <td className="hide-sm">{v.phone || <span className="ci-muted-cell">–</span>}</td>
                  <td className="hide-sm ci-muted-cell">{placeLine(v) || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination"><span className="ci-page-info">{shown.length} {shown.length === 1 ? 'vendor' : 'vendors'}</span></div>
      </div>
    </div>
  )
}
